import { useState, useEffect, useRef } from 'react'
import {
  createCustomer,
  updateCustomer,
  archiveCustomer,
  createCustomerBranch,
  updateCustomerBranch,
  deleteCustomerBranch,
  fetchCustomerTypes,
  uploadPartnerLogo,
  fetchPartnerLogo,
  deletePartnerLogo,
  getCustomerLogoUrl,
  supabase
} from '../supabase-client'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import CustomerTypeComboBox from './CustomerTypeComboBox'
import { PaymentIntegrationSection } from './PaymentIntegrationSection'
import { XeroContactLinkingSection } from './xero/XeroContactLinkingSection'
import PaymentIntegrationModal from './PaymentIntegrationModal'
import AddressFinder from './AddressFinder'
import RecurringOrdersPanel from './RecurringOrdersPanel'
import CustomerCreditNotesPanel from './CustomerCreditNotesPanel'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle, ImagePlus, X, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

const paymentTermsOptions = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 21, label: '21 days' },
  { value: 28, label: '28 days' }
]

const paymentMethodOptions = [
  { value: 'xero', label: 'Xero' },
  { value: 'cash', label: 'Cash' },
  { value: 'gocardless', label: 'GoCardless' }
]

export default function CustomerForm({
  isOpen,
  onClose,
  onSave,
  editingCustomer = null,
  isArchived = false,
  savedFormData = null,
  onFormDataChange = null,
  onUnarchive = null,
  onInvoicing = null
}) {
  const [formData, setFormData] = useState({
    companyName: '',
    phone: '',
    addressLine1: '',
    addressLine2: '',
    addressLine3: '',
    city: '',
    postcode: '',
    country: 'United Kingdom',
    email: '',
    referenceCode: '',
    paymentTermsDays: null,
    paymentMethod: null,
    employeeDiscountEligible: false, // US-014: Employee discount pricing tier
    fruitingBlocksEligible: false,
    growingMaterialsEligible: false,
    transmission: 'delivery',
    billingSource: 'location', // 'location' or 'custom'
    billingLocationId: null, // null = first location, or specific location ID
    branches: [{
      id: `new_branch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      branchName: '',
      branchReferenceCode: '',
      branchTypeId: null,
      branchAddressLine1: '',
      branchAddressLine2: '',
      branchAddressLine3: '',
      branchCity: '',
      branchPostcode: '',
      branchCountry: 'United Kingdom',
      branchPhone: '',
      branchEmail: '',
      branchType: 'branch',
      isExisting: false
    }]
  })
  
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [branchOperationLoading, setBranchOperationLoading] = useState(false)
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false)
  const [showUnarchiveConfirm, setShowUnarchiveConfirm] = useState(false)
  const [customerTypes, setCustomerTypes] = useState([])
  const [showPaymentIntegrationModal, setShowPaymentIntegrationModal] = useState(false)
  const [customerForIntegration, setCustomerForIntegration] = useState(null)
  const [shakeSubmit, setShakeSubmit] = useState(false)
  // Fresh customer data from database — used instead of stale editingCustomer prop
  const [freshCustomerData, setFreshCustomerData] = useState(null)
  const [logoUploading, setLogoUploading] = useState(false)
  const [partnerLogo, setPartnerLogo] = useState(null) // { id, logo_url, logo_url_dark }
  const logoInputRef = useRef(null)
  const prevIsOpenRef = useRef(false)

  // Fetch customer types on component mount
  useEffect(() => {
    const loadCustomerTypes = async () => {
      try {
        const types = await fetchCustomerTypes()
        setCustomerTypes(types)
      } catch (error) {
        console.error('Error loading customer types:', error)
      }
    }
    loadCustomerTypes()
  }, [])

  // Shared function to load fresh customer data from database
  const loadFreshCustomerData = async (customerId) => {
    try {
      const { data: freshData, error } = await supabase
        .from('customers')
        .select(`
          *,
          branches:branches(*)
        `)
        .eq('id', customerId)
        .single()

      if (error) {
        console.error('[CustomerForm] Error fetching fresh customer data:', error)
        throw error
      }

      // Transform fresh data to match expected format
      const transformedCustomer = {
        id: freshData.id,
        companyName: freshData.name,
        email: freshData.email,
        phone: freshData.phone || '',
        paymentTermsDays: freshData.payment_terms ? parseInt(freshData.payment_terms) : 30,
        paymentMethod: freshData.payment_method || null,
        employeeDiscountEligible: freshData.employee_discount_eligible || false, // US-014
        fruitingBlocksEligible: freshData.fruiting_blocks_eligible || false,
        growingMaterialsEligible: freshData.growing_materials_eligible || false,
        transmission: freshData.transmission || 'delivery',
        gocardless_customer_id: freshData.gocardless_customer_id || null,
        gocardless_mandate_id: freshData.gocardless_mandate_id || null,
        gocardless_mandate_status: freshData.gocardless_mandate_status || null,
        referenceCode: freshData.reference_code || '',
        xeroContactId: freshData.xero_contact_id || null,
        xeroContactName: freshData.xero_contact_name || null,
        logoFilePath: freshData.logo_file_path || null,
        logoFileName: freshData.logo_file_name || null,
        logoUploadedAt: freshData.logo_uploaded_at || null,
        branches: freshData.branches?.map(branch => ({
          id: branch.id,
          branchName: branch.name,
          branchReferenceCode: branch.reference_code || '',
          branchAddress: branch.address,
          branchAddressLine1: branch.address_line_1 || '',
          branchAddressLine2: branch.address_line_2 || '',
          branchAddressLine3: branch.address_line_3 || '',
          branchCity: branch.city || '',
          branchPostcode: branch.postcode || '',
          branchCountry: branch.country || 'United Kingdom',
          branchPhone: branch.phone || '',
          branchEmail: branch.email || '',
          branchType: branch.branch_type || 'branch',
          typeId: branch.type_id || null
        })) || []
      }

      return transformedCustomer
    } catch (error) {
      console.error('[CustomerForm] Unexpected error fetching customer:', error)
      throw error
    }
  }

  // Helper function to initialize form from customer object
  const initializeFromProp = (customer) => {
    // Auto-detect payment method based on existing integrations
    let autoPaymentMethod = customer.paymentMethod || null

    // If no payment method set but customer has GoCardless mandate, set to gocardless
    if (!autoPaymentMethod && customer.gocardless_mandate_id) {
      autoPaymentMethod = 'gocardless'
    }

    // Load existing branches (exclude company branches, treat them as regular branches)
    const existingBranches = customer.branches?.map(branch => {
      // Use structured address columns (with fallback to parsing concatenated string for legacy data)
      const hasStructuredAddress = branch.branchAddressLine1 || branch.branchCity || branch.branchPostcode
      let addressLine1, addressLine2, addressLine3, city, postcode, country
      if (hasStructuredAddress) {
        addressLine1 = branch.branchAddressLine1 || ''
        addressLine2 = branch.branchAddressLine2 || ''
        addressLine3 = branch.branchAddressLine3 || ''
        city = branch.branchCity || ''
        postcode = branch.branchPostcode || ''
        country = branch.branchCountry || 'United Kingdom'
      } else {
        // Fallback: parse the concatenated address field
        const addressParts = (branch.branchAddress || '').split(', ').filter(part => part.trim())
        addressLine1 = addressParts[0] || ''
        addressLine2 = addressParts[1] || ''
        addressLine3 = addressParts[2] || ''
        city = addressParts[3] || ''
        postcode = addressParts[4] || ''
        country = addressParts[5] || 'United Kingdom'
      }
      return {
        id: branch.id,
        branchName: branch.branchName || '',
        branchReferenceCode: branch.branchReferenceCode || '',
        branchTypeId: branch.typeId || null,
        branchPhone: branch.branchPhone || '',
        branchAddressLine1: addressLine1,
        branchAddressLine2: addressLine2,
        branchAddressLine3: addressLine3,
        branchCity: city,
        branchPostcode: postcode,
        branchCountry: country,
        branchEmail: branch.branchEmail || '',
        branchType: 'branch', // Always treat as regular branch
        isExisting: true
      }
    }) || []

    // If no branches exist, initialize with one empty branch
    const branches = existingBranches.length > 0 ? existingBranches : [{
      id: `new_branch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      branchName: '',
      branchReferenceCode: '',
      branchTypeId: null,
      branchAddressLine1: '',
      branchAddressLine2: '',
      branchAddressLine3: '',
      branchCity: '',
      branchPostcode: '',
      branchCountry: 'United Kingdom',
      branchPhone: '',
      branchEmail: '',
      branchType: 'branch',
      isExisting: false
    }]

    setFormData({
      companyName: customer.companyName || '',
      phone: customer.phone || '',
      addressLine1: customer.addressLine1 || '',
      addressLine2: customer.addressLine2 || '',
      addressLine3: customer.addressLine3 || '',
      city: customer.city || '',
      postcode: customer.postcode || '',
      country: customer.country || 'United Kingdom',
      email: customer.email || '',
      referenceCode: customer.referenceCode || '',
      paymentTermsDays: customer.paymentTermsDays || 30,
      paymentMethod: autoPaymentMethod,
      employeeDiscountEligible: customer.employeeDiscountEligible || false, // US-014
      fruitingBlocksEligible: customer.fruitingBlocksEligible || false,
      growingMaterialsEligible: customer.growingMaterialsEligible || false,
      transmission: customer.transmission || 'delivery',
      xeroContactId: customer.xeroContactId || null,
      xeroContactName: customer.xeroContactName || null,
      logoFilePath: customer.logoFilePath || null,
      logoFileName: customer.logoFileName || null,
      logoUploadedAt: customer.logoUploadedAt || null,
      billingSource: 'location', // Default to syncing from location
      billingLocationId: null, // null = first location
      branches
    })
  }

  // Initialize form when modal opens
  useEffect(() => {
    if (isOpen) {
      if (editingCustomer) {
        // Refetch fresh customer data from database to ensure correct branch IDs
        const initializeWithFreshData = async () => {
          try {
            const transformedCustomer = await loadFreshCustomerData(editingCustomer.id)
            setFreshCustomerData(transformedCustomer)
            initializeFromProp(transformedCustomer)
          } catch (error) {
            // Fall back to editingCustomer prop if database fetch fails
            setFreshCustomerData(null)
            initializeFromProp(editingCustomer)
          }
        }

        initializeWithFreshData()
      } else if (savedFormData) {
        // Restore saved form data
        setFormData(savedFormData.formData || {
          companyName: '',
          phone: '',
          addressLine1: '',
          addressLine2: '',
          addressLine3: '',
          city: '',
          postcode: '',
          country: 'United Kingdom',
          email: '',
          referenceCode: '',
          paymentTermsDays: null,
          paymentMethod: null,
          employeeDiscountEligible: false, // US-014
          fruitingBlocksEligible: false,
          transmission: 'delivery',
          billingSource: 'location',
          billingLocationId: null,
          branches: [{
            id: `new_branch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            branchName: '',
            branchReferenceCode: '',
            branchTypeId: null,
            branchAddressLine1: '',
            branchAddressLine2: '',
            branchAddressLine3: '',
            branchCity: '',
            branchPostcode: '',
            branchCountry: 'United Kingdom',
            branchPhone: '',
            branchEmail: '',
            branchType: 'branch',
            isExisting: false
          }]
        })
      } else {
        // Reset form for new customer
        setFormData({
          companyName: '',
          phone: '',
          addressLine1: '',
          addressLine2: '',
          addressLine3: '',
          city: '',
          postcode: '',
          country: 'United Kingdom',
          email: '',
          referenceCode: '',
          paymentTermsDays: null,
          paymentMethod: null,
          employeeDiscountEligible: false, // US-014
          fruitingBlocksEligible: false,
          transmission: 'delivery',
          billingSource: 'location',
          billingLocationId: null,
          branches: [{
            id: `new_branch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            branchName: '',
            branchReferenceCode: '',
            branchTypeId: null,
            branchAddressLine1: '',
            branchAddressLine2: '',
            branchAddressLine3: '',
            branchCity: '',
            branchPostcode: '',
            branchCountry: 'United Kingdom',
            branchPhone: '',
            branchEmail: '',
            branchType: 'branch',
            isExisting: false
          }]
        })
      }
      // Only clear errors when the dialog first opens, not on subsequent re-runs
      const justOpened = !prevIsOpenRef.current
      if (justOpened) {
        setErrors({})
      }
    } else {
      // Form closed — reset fresh data
      setFreshCustomerData(null)
    }
    prevIsOpenRef.current = isOpen
  }, [isOpen, editingCustomer?.id, savedFormData])

  // Fetch partner logo when editing a customer
  useEffect(() => {
    if (isOpen && editingCustomer?.id) {
      fetchPartnerLogo(editingCustomer.id).then(setPartnerLogo)
    } else {
      setPartnerLogo(null)
    }
  }, [isOpen, editingCustomer?.id])

  // Save form data when it changes (for persistence) - debounced
  useEffect(() => {
    if (isOpen && !editingCustomer && onFormDataChange) {
      const timeoutId = setTimeout(() => {
        const savedData = {
          formData
        }
        onFormDataChange(savedData)
      }, 300) // 300ms debounce

      return () => clearTimeout(timeoutId)
    }
  }, [formData, isOpen, editingCustomer, onFormDataChange])

  // Sync billing address when billing source or location selection changes
  // NOTE: Billing address syncing from branch fields happens in updateBranch() to avoid
  // validation error flickering. This effect only runs when user switches billing source/location.
  useEffect(() => {
    if (formData.billingSource === 'location' && formData.branches.length > 0) {
      const sourceLocation = formData.billingLocationId
        ? formData.branches.find(b => b.id === formData.billingLocationId)
        : formData.branches[0]

      if (sourceLocation) {
        setFormData(prev => {
          // Only update if values actually differ to prevent infinite loops
          const willUpdate = (
            prev.addressLine1 !== sourceLocation.branchAddressLine1 ||
            prev.addressLine2 !== sourceLocation.branchAddressLine2 ||
            prev.addressLine3 !== sourceLocation.branchAddressLine3 ||
            prev.city !== sourceLocation.branchCity ||
            prev.postcode !== sourceLocation.branchPostcode ||
            prev.country !== sourceLocation.branchCountry
          )

          if (willUpdate) {
            return {
              ...prev,
              addressLine1: sourceLocation.branchAddressLine1,
              addressLine2: sourceLocation.branchAddressLine2,
              addressLine3: sourceLocation.branchAddressLine3,
              city: sourceLocation.branchCity,
              postcode: sourceLocation.branchPostcode,
              country: sourceLocation.branchCountry
            }
          }

          return prev
        })
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.billingSource,
    formData.billingLocationId
  ])

  // Map error keys to DOM element IDs for scroll-to-error
  const getFieldIdForError = (errorKey) => {
    const fieldMap = {
      companyName: 'companyName',
      email: 'email',
      paymentTermsDays: 'paymentTerms',
      paymentMethod: 'paymentMethod',
      branches: 'branches-section',
      xeroIntegration: 'paymentMethod',
      gocardlessIntegration: 'paymentMethod',
    }
    // Direct match
    if (fieldMap[errorKey]) return fieldMap[errorKey]
    // Branch-level errors: branch_0_name -> branch_0_name
    if (errorKey.startsWith('branch_')) return errorKey
    return null
  }

  // Map error keys to section labels for the error banner
  const getSectionForError = (errorKey) => {
    if (['companyName', 'email', 'paymentTermsDays', 'paymentMethod', 'xeroIntegration', 'gocardlessIntegration'].includes(errorKey)) {
      return 'Company Information'
    }
    if (errorKey.startsWith('branch_') || errorKey === 'branches') {
      return 'Delivery Locations'
    }
    return null
  }

  const scrollToFirstError = () => {
    // Scroll dialog to top so the error banner is visible - user can click links to jump to fields
    requestAnimationFrame(() => {
      const dialogContent = document.querySelector('[data-slot="dialog-content"]')
      if (dialogContent) {
        dialogContent.scrollTo({ top: 0, behavior: 'smooth' })
      }
    })
  }

  const validateForm = () => {
    const newErrors = {}

    // Company name is required
    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required'
    }

    // Email is required
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    // Payment terms validation
    if (!formData.paymentTermsDays) {
      newErrors.paymentTermsDays = 'Payment terms is required'
    } else if (formData.paymentTermsDays < 0) {
      newErrors.paymentTermsDays = 'Payment terms must be a positive number'
    }

    // Payment method validation - only for existing customers
    if (editingCustomer && !formData.paymentMethod) {
      newErrors.paymentMethod = 'Payment method is required'
    }

    // Integration validation - require linking for Xero and GoCardless
    if (formData.paymentMethod === 'xero' && editingCustomer) {
      // Check customer-level Xero contact mapping
      if (!formData.xeroContactId) {
        newErrors.xeroIntegration = 'Customer must be linked to a Xero contact'
      }
    }

    if (formData.paymentMethod === 'gocardless' && editingCustomer) {
      // Use fresh data (from DB) over stale editingCustomer prop
      const customerData = freshCustomerData || editingCustomer
      if (!customerData.gocardless_mandate_id) {
        newErrors.gocardlessIntegration = 'You must link a GoCardless mandate before saving'
      }
    }

    // Require at least one delivery location
    if (formData.branches.length === 0) {
      newErrors.branches = 'At least one delivery location is required'
    }

    // Validate each branch
    formData.branches.forEach((branch, index) => {
      if (!branch.branchName.trim()) {
        newErrors[`branch_${index}_name`] = 'Location name is required'
      }
      if (!branch.branchTypeId) {
        newErrors[`branch_${index}_type`] = 'Customer type is required for this location'
      }
      if (!branch.branchAddressLine1.trim()) {
        newErrors[`branch_${index}_address`] = 'Address is required for this location'
      }
    })

    setErrors(newErrors)
    return newErrors
  }

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file || !editingCustomer) return

    // Reset input so same file can be re-selected
    e.target.value = ''

    setLogoUploading(true)
    try {
      // Deactivate existing partner logo if present
      if (partnerLogo?.id) {
        await deletePartnerLogo(partnerLogo.id)
      }

      // Upload via GMC processing pipeline
      const result = await uploadPartnerLogo(file, editingCustomer.id)

      setPartnerLogo({
        id: result.id,
        logo_url: result.logo_url,
        logo_url_dark: result.logo_url_dark
      })

      toast.success('Logo uploaded')
    } catch (error) {
      toast.error(error.message || 'Failed to upload logo')
    } finally {
      setLogoUploading(false)
    }
  }

  const handleLogoRemove = async () => {
    if (!editingCustomer) return

    setLogoUploading(true)
    try {
      if (partnerLogo?.id) {
        await deletePartnerLogo(partnerLogo.id)
        setPartnerLogo(null)
      }

      toast.success('Logo removed')
    } catch (error) {
      toast.error('Failed to remove logo')
    } finally {
      setLogoUploading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    const validationErrors = validateForm()
    if (Object.keys(validationErrors).length > 0) {
      setShakeSubmit(true)
      setTimeout(() => setShakeSubmit(false), 500)
      scrollToFirstError()
      return
    }

    setLoading(true)

    try {
      // Prepare customer data for database
      const customerData = {
        companyName: formData.companyName.trim(),
        email: formData.email.trim(),
        phone: formData.phone.trim(),
        paymentTerms: formData.paymentTermsDays.toString(),
        employeeDiscountEligible: formData.employeeDiscountEligible, // US-014
        fruitingBlocksEligible: formData.fruitingBlocksEligible,
        growingMaterialsEligible: formData.growingMaterialsEligible,
        transmission: formData.transmission,
        referenceCode: formData.referenceCode?.trim() || null
      }

      // Only include payment method for existing customers
      if (editingCustomer) {
        customerData.paymentMethod = formData.paymentMethod
      }

      let savedCustomer

      if (editingCustomer) {
        // Update existing customer
        savedCustomer = await updateCustomer(editingCustomer.id, customerData)
      } else {
        // Create new customer WITHOUT payment method
        savedCustomer = await createCustomer(customerData)

        // GET the customer to ensure we have the ID and latest data
        const { data: freshCustomer, error: fetchError } = await supabase
          .from('customers')
          .select('*')
          .eq('id', savedCustomer.id)
          .single()

        if (fetchError) {
          throw new Error('Failed to retrieve created customer: ' + fetchError.message)
        }

        savedCustomer = freshCustomer
      }

      // Handle branches
      const existingBranches = formData.branches.filter(branch => branch.isExisting)
      const newBranches = formData.branches.filter(branch => !branch.isExisting)

      // Update existing branches
      for (const branch of existingBranches) {
        if (branch.branchName.trim()) {
          const branchData = {
            branchName: branch.branchName.trim(),
            address: [
              branch.branchAddressLine1,
              branch.branchAddressLine2,
              branch.branchAddressLine3,
              branch.branchCity,
              branch.branchPostcode,
              branch.branchCountry
            ].filter(line => line && line.trim()).join(', '),
            address_line_1: branch.branchAddressLine1?.trim() || null,
            address_line_2: branch.branchAddressLine2?.trim() || null,
            address_line_3: branch.branchAddressLine3?.trim() || null,
            city: branch.branchCity?.trim() || null,
            postcode: branch.branchPostcode?.trim() || null,
            country: branch.branchCountry?.trim() || 'United Kingdom',
            phone: branch.branchPhone?.trim() || '',
            email: branch.branchEmail?.trim() || '',
            branch_type: 'branch',
            type_id: branch.branchTypeId,
            reference_code: branch.branchReferenceCode?.trim() || null
          }

          await updateCustomerBranch(branch.id, branchData)
        }
      }

      // Create new branches
      for (const branch of newBranches) {
        if (branch.branchName.trim()) {
          const branchData = {
            customer_id: savedCustomer.id,
            branchName: branch.branchName.trim(),
            address: [
              branch.branchAddressLine1,
              branch.branchAddressLine2,
              branch.branchAddressLine3,
              branch.branchCity,
              branch.branchPostcode,
              branch.branchCountry
            ].filter(line => line && line.trim()).join(', '),
            address_line_1: branch.branchAddressLine1?.trim() || null,
            address_line_2: branch.branchAddressLine2?.trim() || null,
            address_line_3: branch.branchAddressLine3?.trim() || null,
            city: branch.branchCity?.trim() || null,
            postcode: branch.branchPostcode?.trim() || null,
            country: branch.branchCountry?.trim() || 'United Kingdom',
            phone: branch.branchPhone?.trim() || '',
            email: branch.branchEmail?.trim() || '',
            branch_type: 'branch',
            type_id: branch.branchTypeId,
            reference_code: branch.branchReferenceCode?.trim() || null
          }

          await createCustomerBranch(branchData)
        }
      }

      // Pass the complete customer data back to parent
      const completeCustomerData = {
        ...customerData,
        id: savedCustomer.id,
        branches: formData.branches.filter(branch => branch.branchName.trim())
      }

      // After branch creation for NEW customers, show payment integration modal
      if (!editingCustomer) {
        setCustomerForIntegration(savedCustomer)
        setShowPaymentIntegrationModal(true)
        // Don't call onClose() yet - let the payment modal handle that
        return
      }

      // Sync changes to Xero (fire-and-forget)
      if (formData.xeroContactId) {
        const originalEmail = editingCustomer.email || ''
        const originalPhone = editingCustomer.phone || ''
        const originalName = editingCustomer.companyName || ''
        const newEmail = formData.email.trim()
        const newPhone = formData.phone?.trim() || ''
        const newName = formData.companyName.trim()

        // Customer-level changes → update all mapped branches
        const originalPaymentTerms = editingCustomer.paymentTermsDays || 30
        const newPaymentTerms = formData.paymentTermsDays || 28
        const customerFields = {}
        if (originalEmail !== newEmail) {
          customerFields.EmailAddress = newEmail
        }
        if (originalPhone !== newPhone) {
          customerFields.Phones = [{ PhoneType: 'DEFAULT', PhoneNumber: newPhone }]
        }
        if (originalPaymentTerms !== newPaymentTerms) {
          customerFields.PaymentTerms = { Sales: { Day: newPaymentTerms, Type: 'DAYSAFTERBILLDATE' } }
        }

        if (Object.keys(customerFields).length > 0) {
          const toastId = toast.loading('Syncing to Xero...')
          supabase.functions.invoke('xero-contacts', {
            body: { action: 'update', customerId: editingCustomer.id, fields: customerFields }
          }).then(({ data, error }) => {
            if (error) {
              toast.error('Failed to sync to Xero', { id: toastId, description: error.message })
            } else if (data?.success === false) {
              toast.warning(data.message, { id: toastId })
            } else {
              toast.success(data?.message || 'Xero contacts updated', { id: toastId })
            }
          }).catch(() => {
            toast.error('Failed to sync to Xero', { id: toastId })
          })
        }

        // Customer name change → update Xero contact name
        if (originalName !== newName) {
          supabase.functions.invoke('xero-contacts', {
            body: { action: 'update', customerId: editingCustomer.id, fields: { Name: newName } }
          }).catch(err => {
            console.error('Failed to sync customer name to Xero:', err)
          })
        }
      }

      // For existing customers, proceed as normal
      await onSave?.(completeCustomerData)
      onClose()
    } catch (error) {
      console.error('Error saving customer:', error)
      const errorMessage = error.message || 'Failed to save customer. Please try again.'
      setErrors({ submit: errorMessage })
    } finally {
      setLoading(false)
    }
  }

  const handleArchiveCustomer = async () => {
    if (!editingCustomer) return
    
    setLoading(true)
    try {
      const archivedCustomer = await archiveCustomer(editingCustomer.id)
      
      // Create customer data structure that parent expects
      const customerData = {
        id: archivedCustomer.id,
        companyName: archivedCustomer.name,
        email: archivedCustomer.email,
        paymentTermsDays: parseInt(archivedCustomer.payment_terms) || 30,
        branches: editingCustomer.branches || []
      }
      
      await onSave?.(customerData)
      onClose()
    } catch (error) {
      console.error('Error archiving customer:', error)
      const errorMessage = error.message || 'Failed to archive customer. Please try again.'
      setErrors({ submit: errorMessage })
    } finally {
      setLoading(false)
      setShowArchiveConfirm(false)
    }
  }

  const handleUnarchiveCustomer = async () => {
    if (!editingCustomer) return
    
    setLoading(true)
    try {
      await onUnarchive(editingCustomer.id)
      onClose()
    } catch (error) {
      console.error('Error unarchiving customer:', error)
      const errorMessage = error.message || 'Failed to unarchive customer. Please try again.'
      setErrors({ submit: errorMessage })
    } finally {
      setLoading(false)
      setShowUnarchiveConfirm(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }))
    }
  }

  const addBranch = () => {
    const newBranch = {
      id: `new_branch_${Date.now()}_${(() => {
        const randomValue = Math.random();
        const stringValue = randomValue.toString(36);
        return stringValue.substr(2, 9);
      })()}`, // More unique temporary ID
      branchName: '',
      branchReferenceCode: '',
      branchTypeId: null, // Customer type for this branch
      branchAddressLine1: '',
      branchAddressLine2: '',
      branchAddressLine3: '',
      branchCity: '',
      branchPostcode: '',
      branchCountry: 'United Kingdom',
      branchPhone: '',
      branchEmail: '',
      branchType: 'branch', // Set as regular branch (not company)
      isExisting: false // Flag to track new branches
    }

    setFormData(prev => ({
      ...prev,
      branches: [...prev.branches, newBranch]
    }))
  }

  const updateBranch = (branchId, field, value) => {
    setFormData(prev => {
      const updatedBranches = prev.branches.map(branch =>
        branch.id === branchId
          ? { ...branch, [field]: value }
          : branch
      )

      // If billing is set to sync from locations and we're updating the billing source location,
      // also update the billing address fields
      const isBillingSource = prev.billingSource === 'location' && (
        prev.billingLocationId === branchId ||
        (prev.billingLocationId === null && prev.branches[0]?.id === branchId)
      )

      if (isBillingSource && field.startsWith('branch')) {
        // Map branch field names to billing field names
        const billingFieldMap = {
          branchAddressLine1: 'addressLine1',
          branchAddressLine2: 'addressLine2',
          branchAddressLine3: 'addressLine3',
          branchCity: 'city',
          branchPostcode: 'postcode',
          branchCountry: 'country'
        }

        const billingField = billingFieldMap[field]
        if (billingField) {
          return {
            ...prev,
            branches: updatedBranches,
            [billingField]: value
          }
        }
      }

      return {
        ...prev,
        branches: updatedBranches
      }
    })

    // Clear branch-specific errors when user starts typing
    if (field === 'branchAddressLine1' || field === 'branchName' || field === 'branchTypeId') {
      const branchIndex = formData.branches.findIndex(b => b.id === branchId)
      const errorKeys = [
        `branch_${branchIndex}_address`,
        `branch_${branchIndex}_name`,
        `branch_${branchIndex}_type`
      ]

      const hasErrors = errorKeys.some(key => errors[key])
      if (hasErrors) {
        setErrors(prev => {
          const newErrors = { ...prev }
          errorKeys.forEach(key => {
            newErrors[key] = undefined
          })
          return newErrors
        })
      }
    }
  }

  const removeBranch = async (branchId) => {
    // Prevent removing last branch
    if (formData.branches.length === 1) {
      setErrors({ submit: 'Cannot remove the last delivery location. At least one location is required.' })
      return
    }

    // Check if this is an existing branch that needs to be deleted from database
    const branch = formData.branches.find(b => b.id === branchId)

    if (branch && branch.isExisting) {
      if (!confirm('Are you sure you want to delete this branch? This action cannot be undone.')) {
        return
      }

      setBranchOperationLoading(true)
      try {
        await deleteCustomerBranch(branchId)
      } catch (error) {
        console.error('Error deleting branch:', error)
        setErrors({ submit: 'Failed to delete branch. Please try again.' })
        setBranchOperationLoading(false)
        return
      }
      setBranchOperationLoading(false)
    }

    // Handle billing source if removing the billing location
    const newBranches = formData.branches.filter(b => b.id !== branchId)
    const wasBillingSource = formData.billingLocationId === branchId ||
                             (formData.billingLocationId === null && formData.branches[0]?.id === branchId)

    // Remove from local state
    setFormData(prev => ({
      ...prev,
      branches: newBranches,
      // If we removed the billing source, switch to the next available location
      billingLocationId: wasBillingSource ? null : prev.billingLocationId
    }))
  }

  /**
   * Refresh customer Xero contact mapping data from database
   * Called after linking/unlinking/creating Xero contacts
   */
  const refreshCustomerXeroMapping = async () => {
    try {
      const customerId = editingCustomer?.id
      if (!customerId) return

      const { data, error } = await supabase
        .from('customers')
        .select('xero_contact_id, xero_contact_name')
        .eq('id', customerId)
        .single()

      if (error) throw error

      setFormData(prev => ({
        ...prev,
        xeroContactId: data.xero_contact_id || null,
        xeroContactName: data.xero_contact_name || null
      }))
    } catch (error) {
      console.error('[CustomerForm] Failed to refresh Xero mapping:', error)
      toast.error('Failed to refresh Xero contact data', {
        description: 'The contact was linked, but the display may not have updated. Try refreshing the page.'
      })
    }
  }


  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto sm:max-w-6xl">
        <DialogHeader>
          <DialogTitle>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          {/* Top Error Summary Banner - always visible regardless of scroll position */}
          {Object.keys(errors).filter(k => errors[k] && k !== 'submit').length > 0 && (() => {
            const validationErrors = Object.entries(errors).filter(([k, v]) => v && k !== 'submit')
            const grouped = {}
            validationErrors.forEach(([key, message]) => {
              const section = getSectionForError(key) || 'Other'
              if (!grouped[section]) grouped[section] = []
              grouped[section].push({ key, message })
            })
            return (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <p className="font-medium mb-2">
                    Please fix {validationErrors.length} issue{validationErrors.length > 1 ? 's' : ''} before saving
                  </p>
                  {Object.entries(grouped).map(([section, items]) => (
                    <div key={section} className="mb-1">
                      <p className="text-xs font-semibold uppercase tracking-wide opacity-80 mb-0.5">{section}</p>
                      <ul className="list-disc pl-4 space-y-0.5">
                        {items.map(({ key, message }) => {
                          const elementId = getFieldIdForError(key)
                          return (
                            <li key={key}>
                              {elementId ? (
                                <button
                                  type="button"
                                  className="underline hover:no-underline text-left"
                                  onClick={() => {
                                    const el = document.getElementById(elementId)
                                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                                  }}
                                >
                                  {message}
                                </button>
                              ) : (
                                message
                              )}
                            </li>
                          )
                        })}
                      </ul>
                    </div>
                  ))}
                </AlertDescription>
              </Alert>
            )
          })()}

          <Card className={`relative ${Object.keys(errors).some(k => errors[k] && ['companyName', 'email', 'paymentTermsDays', 'paymentMethod', 'xeroIntegration', 'gocardlessIntegration'].includes(k)) ? 'border-red-300 ring-1 ring-red-200' : ''}`}>
            {editingCustomer && (
              <div className="absolute top-5 right-5 z-10">
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/avif"
                  className="hidden"
                  onChange={handleLogoUpload}
                />
                {(() => {
                  const logoUrl = partnerLogo?.logo_url_dark || getCustomerLogoUrl(formData.logoFilePath)
                  const hasLogo = !!logoUrl
                  return hasLogo ? (
                    <div className="group relative">
                      <img
                        src={logoUrl}
                        alt="Logo"
                        className="h-40 w-40 rounded-lg object-contain ring-1 ring-border cursor-pointer bg-white"
                        onClick={() => logoInputRef.current?.click()}
                      />
                      <button
                        type="button"
                        onClick={handleLogoRemove}
                        disabled={logoUploading}
                        className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground opacity-0 transition-opacity group-hover:opacity-100"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={logoUploading}
                      className="flex h-40 w-40 flex-col items-center justify-center gap-1 rounded-lg border-2 border-dashed border-muted-foreground/30 text-muted-foreground hover:border-muted-foreground/50 hover:text-muted-foreground/70 transition-colors"
                    >
                      {logoUploading ? (
                        <Loader2 className="h-6 w-6 animate-spin" />
                      ) : (
                        <>
                          <ImagePlus className="h-6 w-6" />
                          <span className="text-xs">Add Logo</span>
                        </>
                      )}
                    </button>
                  )
                })()}
              </div>
            )}
            <CardHeader>
              <div className="flex items-center gap-3">
                <CardTitle>Company Information</CardTitle>
                <AddressFinder
                  businessName={formData.companyName}
                  onAddressSelect={(addressData) => {
                    // Update form fields with address data from Google Places
                    setFormData(prev => ({
                      ...prev,
                      addressLine1: addressData.addressLine1,
                      addressLine2: addressData.addressLine2,
                      addressLine3: addressData.addressLine3,
                      city: addressData.city,
                      postcode: addressData.postcode,
                      country: addressData.country,
                      phone: addressData.phone
                    }))
                  }}
                  disabled={loading}
                  variant="company"
                />
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="companyName">
                  Company Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="companyName"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  placeholder="Enter company name"
                  style={{ maxWidth: '65ch' }}
                  className={errors.companyName ? 'border-red-500' : ''}
                />
                {errors.companyName && <p className="text-red-500 text-sm">{errors.companyName}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="referenceCode">Invoice Ref Code</Label>
                <Input
                  id="referenceCode"
                  value={formData.referenceCode}
                  onChange={(e) => handleInputChange('referenceCode', e.target.value.toUpperCase().slice(0, 6))}
                  placeholder={formData.companyName ? formData.companyName.substring(0, 4).toUpperCase() : 'AUTO'}
                  maxLength={6}
                  className="w-32 uppercase"
                />
                <p className="text-xs text-muted-foreground">Used on Xero invoice references (max 6 chars)</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email Address <span className="text-red-500">*</span></Label>
                  <Input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    placeholder="customer@example.com"
                    className={errors.email ? 'border-red-500' : ''}
                  />
                  {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    type="tel"
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="+44 20 1234 5678"
                  />
                </div>
              </div>

              {/* Adaptive Billing Source Selector */}
              <div className="space-y-2">
                <Label htmlFor="billingAddress">Billing Address</Label>

                {formData.branches.length === 1 ? (
                  // Single location: Show checkbox + transmission dropdown
                  <div className="flex items-center justify-between px-1 py-2">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="syncBillingAddress"
                        checked={formData.billingSource === 'location'}
                        onChange={(e) => {
                          const syncing = e.target.checked
                          if (syncing) {
                            // Sync from first location
                            const firstBranch = formData.branches[0]
                            setFormData(prev => ({
                              ...prev,
                              billingSource: 'location',
                              billingLocationId: null, // null = first location
                              addressLine1: firstBranch.branchAddressLine1,
                              addressLine2: firstBranch.branchAddressLine2,
                              addressLine3: firstBranch.branchAddressLine3,
                              city: firstBranch.branchCity,
                              postcode: firstBranch.branchPostcode,
                              country: firstBranch.branchCountry
                            }))
                          } else {
                            setFormData(prev => ({
                              ...prev,
                              billingSource: 'custom',
                              billingLocationId: null
                            }))
                          }
                        }}
                        className="h-4 w-4 mt-1 text-green-600 rounded border-gray-300 focus:ring-green-500 focus:ring-offset-0"
                      />
                      <label htmlFor="syncBillingAddress" className="cursor-pointer select-none">
                        <span className="text-sm font-medium text-gray-900">Use same address as delivery location</span>
                        <span className="block text-sm text-gray-500 mt-0.5">
                          Billing address will automatically sync with your delivery location
                        </span>
                      </label>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-gray-500">Transmission</Label>
                      <Select value={formData.transmission} onValueChange={(value) => setFormData(prev => ({ ...prev, transmission: value }))}>
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="delivery">Delivery</SelectItem>
                          <SelectItem value="collection">Collection</SelectItem>
                          <SelectItem value="courier">Courier</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  // Multiple locations: Show billing dropdown + transmission
                  <div className="flex items-center gap-3">
                    <Select
                      value={formData.billingSource === 'custom' ? 'custom' : (formData.billingLocationId || formData.branches[0]?.id || 'custom')}
                      onValueChange={(value) => {
                        if (value === 'custom') {
                          setFormData(prev => ({
                            ...prev,
                            billingSource: 'custom',
                            billingLocationId: null
                          }))
                        } else {
                          // Sync from selected location
                          const selectedBranch = formData.branches.find(b => b.id === value)
                          if (selectedBranch) {
                            setFormData(prev => ({
                              ...prev,
                              billingSource: 'location',
                              billingLocationId: value,
                              addressLine1: selectedBranch.branchAddressLine1,
                              addressLine2: selectedBranch.branchAddressLine2,
                              addressLine3: selectedBranch.branchAddressLine3,
                              city: selectedBranch.branchCity,
                              postcode: selectedBranch.branchPostcode,
                              country: selectedBranch.branchCountry
                            }))
                          }
                        }
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select billing address source" />
                      </SelectTrigger>
                      <SelectContent>
                        {formData.branches.map((branch) => (
                          <SelectItem key={branch.id} value={branch.id}>
                            {branch.branchName || `Location ${formData.branches.indexOf(branch) + 1}`}
                          </SelectItem>
                        ))}
                        <SelectItem value="custom">Use different address</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="space-y-1 shrink-0">
                      <Label className="text-xs text-gray-500">Transmission</Label>
                      <Select value={formData.transmission} onValueChange={(value) => setFormData(prev => ({ ...prev, transmission: value }))}>
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="delivery">Delivery</SelectItem>
                          <SelectItem value="collection">Collection</SelectItem>
                          <SelectItem value="courier">Courier</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>

              {/* Address Fields - disabled when syncing */}
              <div className="space-y-2">
                <Label htmlFor="addressLine1">Address Line 1</Label>
                <Input
                  id="addressLine1"
                  value={formData.addressLine1}
                  onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                  placeholder="Address Line 1"
                  disabled={formData.billingSource === 'location'}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  id="addressLine2"
                  value={formData.addressLine2}
                  onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                  placeholder="Address Line 2 (optional)"
                  disabled={formData.billingSource === 'location'}
                />
                <Input
                  id="addressLine3"
                  value={formData.addressLine3}
                  onChange={(e) => handleInputChange('addressLine3', e.target.value)}
                  placeholder="Address Line 3 (optional)"
                  disabled={formData.billingSource === 'location'}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="City, Town, or Village"
                  disabled={formData.billingSource === 'location'}
                />
                <Input
                  id="postcode"
                  value={formData.postcode}
                  onChange={(e) => handleInputChange('postcode', e.target.value)}
                  placeholder="Postcode"
                  disabled={formData.billingSource === 'location'}
                />
              </div>

              <Input
                id="country"
                value={formData.country}
                onChange={(e) => handleInputChange('country', e.target.value)}
                placeholder="Country"
                disabled={formData.billingSource === 'location'}
              />

              <div className="grid grid-cols-1 md:grid-cols-[55%_45%] gap-4 mt-2">
                {/* Left Column: Payment Terms */}
                <div className="space-y-2" id="paymentTerms">
                  <Label htmlFor="paymentTerms">
                    Payment Terms <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.paymentTermsDays?.toString() || ''}
                    onValueChange={(value) => handleInputChange('paymentTermsDays', parseInt(value) || 0)}
                  >
                    <SelectTrigger className={errors.paymentTermsDays ? 'border-red-500' : ''}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {paymentTermsOptions.map(option => (
                        <SelectItem key={option.value} value={option.value.toString()}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.paymentTermsDays && <p className="text-red-500 text-sm">{errors.paymentTermsDays}</p>}
                </div>

                {/* Right Column: Payment Method and Integration */}
                {editingCustomer && (
                  <div className="space-y-2" id="paymentMethod">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="paymentMethod">
                        Payment Method <span className="text-red-500">*</span>
                      </Label>
                      {!formData.paymentMethod && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setCustomerForIntegration(editingCustomer)
                            setShowPaymentIntegrationModal(true)
                          }}
                        >
                          Set Up Payment Method
                        </Button>
                      )}
                    </div>
                    <Select
                      value={formData.paymentMethod || ''}
                      onValueChange={(value) => handleInputChange('paymentMethod', value)}
                    >
                      <SelectTrigger className={errors.paymentMethod ? 'border-red-500' : ''}>
                        <SelectValue placeholder="Select payment method" />
                      </SelectTrigger>
                      <SelectContent>
                        {paymentMethodOptions.map(option => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {errors.paymentMethod && <p className="text-red-500 text-sm">{errors.paymentMethod}</p>}

                  {/* Payment Integration Section - in right column below payment method */}
                  {formData.paymentMethod && formData.paymentMethod !== 'cash' && (
                    <div className="mt-4 pt-4 border-t">
                      <PaymentIntegrationSection
                        paymentMethod={formData.paymentMethod}
                        odinCustomer={{ ...(freshCustomerData || editingCustomer), name: (freshCustomerData || editingCustomer)?.companyName }}
                        gocardlessMandateId={(freshCustomerData || editingCustomer)?.gocardless_mandate_id}
                        gocardlessMandateStatus={(freshCustomerData || editingCustomer)?.gocardless_mandate_status}
                        gocardlessCustomerId={(freshCustomerData || editingCustomer)?.gocardless_customer_id}
                        onMappingChange={async () => {
                          // Refresh fresh customer data after mapping changes
                          if (editingCustomer?.id) {
                            try {
                              const fresh = await loadFreshCustomerData(editingCustomer.id)
                              setFreshCustomerData(fresh)
                            } catch (e) {
                              console.error('[CustomerForm] Failed to refresh after mapping change:', e)
                            }
                          }
                        }}
                      />
                      {/* Xero Contact Linking - Customer Level */}
                      {(formData.paymentMethod === 'xero' || formData.paymentMethod === 'gocardless') && editingCustomer?.id && (
                        <div className="mt-4 pt-4 border-t">
                          <Label className="mb-2 block">Xero Contact</Label>
                          <XeroContactLinkingSection
                            xeroContactId={formData.xeroContactId || null}
                            xeroContactName={formData.xeroContactName || null}
                            odinCustomer={{
                              id: editingCustomer.id,
                              name: formData.companyName,
                              email: formData.email,
                              phone: formData.phone
                            }}
                            onMappingChange={() => {
                              refreshCustomerXeroMapping()
                            }}
                          />
                        </div>
                      )}
                      {errors.xeroIntegration && (
                        <p className="text-red-500 text-sm mt-2">{errors.xeroIntegration}</p>
                      )}
                      {errors.gocardlessIntegration && (
                        <p className="text-red-500 text-sm mt-2">{errors.gocardlessIntegration}</p>
                      )}
                    </div>
                  )}
                  </div>
                )}
              </div>

              {/* US-014: Employee Discount Eligibility */}
              <div className="space-y-2 pt-4 mt-4 border-t">
                <div className="flex items-start gap-3 px-1 py-2">
                  <input
                    type="checkbox"
                    id="employeeDiscountEligible"
                    checked={formData.employeeDiscountEligible}
                    onChange={(e) => handleInputChange('employeeDiscountEligible', e.target.checked)}
                    className="h-4 w-4 mt-1 text-green-600 rounded border-gray-300 focus:ring-green-500 focus:ring-offset-0"
                  />
                  <label htmlFor="employeeDiscountEligible" className="cursor-pointer select-none">
                    <span className="text-sm font-medium text-gray-900">Employee Discount Eligible</span>
                    <span className="block text-sm text-gray-500 mt-0.5">
                      When enabled, this customer qualifies for staff pricing (typically 25% discount).
                      Applicable for employees purchasing mushrooms for personal use.
                    </span>
                  </label>
                </div>
              </div>

              {/* Fruiting Blocks Eligibility */}
              <div className="space-y-2 pt-4 mt-4 border-t">
                <div className="flex items-start gap-3 px-1 py-2">
                  <input
                    type="checkbox"
                    id="fruitingBlocksEligible"
                    checked={formData.fruitingBlocksEligible}
                    onChange={(e) => handleInputChange('fruitingBlocksEligible', e.target.checked)}
                    className="h-4 w-4 mt-1 text-green-600 rounded border-gray-300 focus:ring-green-500 focus:ring-offset-0"
                  />
                  <label htmlFor="fruitingBlocksEligible" className="cursor-pointer select-none">
                    <span className="text-sm font-medium text-gray-900">Fruiting Blocks Eligible</span>
                    <span className="block text-sm text-gray-500 mt-0.5">
                      When enabled, this customer can order fruiting blocks alongside fresh mushrooms.
                    </span>
                  </label>
                </div>
              </div>

              {/* Growing Materials Eligibility */}
              <div className="space-y-2 pt-4 mt-4 border-t">
                <div className="flex items-start gap-3 px-1 py-2">
                  <input
                    type="checkbox"
                    id="growingMaterialsEligible"
                    checked={formData.growingMaterialsEligible}
                    onChange={(e) => handleInputChange('growingMaterialsEligible', e.target.checked)}
                    className="h-4 w-4 mt-1 text-green-600 rounded border-gray-300 focus:ring-green-500 focus:ring-offset-0"
                  />
                  <label htmlFor="growingMaterialsEligible" className="cursor-pointer select-none">
                    <span className="text-sm font-medium text-gray-900">Growing Materials Eligible</span>
                    <span className="block text-sm text-gray-500 mt-0.5">
                      When enabled, this customer can order growing materials (substrate logs, spawn, straw pellets) alongside fresh mushrooms.
                    </span>
                  </label>
                </div>
              </div>

              {/* Hint for new customers about post-save setup */}
              {!editingCustomer && (
                <p className="text-sm text-gray-400 italic pt-2">
                  Payment method and Xero contact setup will be available after saving.
                </p>
              )}
            </CardContent>
          </Card>

          <Card id="branches-section" className={Object.keys(errors).some(k => errors[k] && (k.startsWith('branch_') || k === 'branches')) ? 'border-red-300 ring-1 ring-red-200' : ''}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Delivery Locations</CardTitle>
                  <p className="text-sm text-gray-500 mt-1">
                    Add all locations where you deliver to this customer. At least one location is required.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="create"
                  onClick={addBranch}
                >
                  + Add Location
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* User-added branches */}
                    {formData.branches.map((branch, index) => (
                      <Card key={branch.id} className="border">
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <CardTitle className="text-lg">Location {index + 1}</CardTitle>
                              <AddressFinder
                                businessName={branch.branchName}
                                onAddressSelect={(addressData) => {
                                  // Update branch fields with address data from Google Places
                                  setFormData(prev => ({
                                    ...prev,
                                    branches: prev.branches.map(b =>
                                      b.id === branch.id
                                        ? {
                                            ...b,
                                            branchAddressLine1: addressData.addressLine1,
                                            branchAddressLine2: addressData.addressLine2,
                                            branchAddressLine3: addressData.addressLine3,
                                            branchCity: addressData.city,
                                            branchPostcode: addressData.postcode,
                                            branchCountry: addressData.country,
                                            branchPhone: addressData.phone
                                          }
                                        : b
                                    )
                                  }))
                                }}
                                disabled={branchOperationLoading}
                                variant="branch"
                              />
                            </div>
                            <Button
                              type="button"
                              variant="destructive"
                              size="sm"
                              onClick={() => removeBranch(branch.id)}
                              disabled={branchOperationLoading}
                            >
                              {branchOperationLoading ? 'Removing...' : 'Remove'}
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-4">

                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2 md:col-span-2" id={`branch_${index}_name`}>
                              <Label>
                                Branch Name <span className="text-red-500">*</span>
                              </Label>
                              <Input
                                value={branch.branchName}
                                onChange={(e) => updateBranch(branch.id, 'branchName', e.target.value)}
                                placeholder="Main Branch"
                                className={errors[`branch_${index}_name`] ? 'border-red-500' : ''}
                              />
                              {errors[`branch_${index}_name`] && (
                                <p className="text-red-500 text-sm">{errors[`branch_${index}_name`]}</p>
                              )}
                            </div>

                            <div className="space-y-2">
                              <Label>Invoice Ref</Label>
                              <Input
                                value={branch.branchReferenceCode}
                                onChange={(e) => updateBranch(branch.id, 'branchReferenceCode', e.target.value.toUpperCase().slice(0, 6))}
                                placeholder={branch.branchName ? branch.branchName.substring(0, 3).toUpperCase() : 'AUTO'}
                                maxLength={6}
                                className="uppercase"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Branch Phone</Label>
                              <Input
                                type="tel"
                                value={branch.branchPhone}
                                onChange={(e) => updateBranch(branch.id, 'branchPhone', e.target.value)}
                                placeholder="+44 20 1234 5678"
                              />
                            </div>
                          </div>

                          <div className="space-y-2" id={`branch_${index}_type`}>
                            <Label htmlFor={`branchType_${branch.id}`}>
                              Customer Type <span className="text-red-500">*</span>
                            </Label>
                            <CustomerTypeComboBox
                              value={branch.branchTypeId}
                              onChange={(typeId) => updateBranch(branch.id, 'branchTypeId', typeId)}
                              disabled={loading}
                              error={!!errors[`branch_${index}_type`]}
                            />
                            {errors[`branch_${index}_type`] && (
                              <p className="text-red-500 text-sm">{errors[`branch_${index}_type`]}</p>
                            )}
                          </div>

                          <div className="space-y-2" id={`branch_${index}_address`}>
                            <Label>
                              Branch Address Line 1 <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              value={branch.branchAddressLine1}
                              onChange={(e) => updateBranch(branch.id, 'branchAddressLine1', e.target.value)}
                              placeholder="Address Line 1"
                              className={errors[`branch_${index}_address`] ? 'border-red-500' : ''}
                            />
                            {errors[`branch_${index}_address`] && (
                              <p className="text-red-500 text-sm">{errors[`branch_${index}_address`]}</p>
                            )}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Address Line 2</Label>
                              <Input
                                value={branch.branchAddressLine2}
                                onChange={(e) => updateBranch(branch.id, 'branchAddressLine2', e.target.value)}
                                placeholder="Address Line 2 (optional)"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label>Address Line 3</Label>
                              <Input
                                value={branch.branchAddressLine3}
                                onChange={(e) => updateBranch(branch.id, 'branchAddressLine3', e.target.value)}
                                placeholder="Address Line 3 (optional)"
                              />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>City/Town/Village</Label>
                              <Input
                                value={branch.branchCity}
                                onChange={(e) => updateBranch(branch.id, 'branchCity', e.target.value)}
                                placeholder="City, Town, or Village"
                              />
                            </div>

                            <div className="space-y-2">
                              <Label>Postcode</Label>
                              <Input
                                value={branch.branchPostcode}
                                onChange={(e) => updateBranch(branch.id, 'branchPostcode', e.target.value)}
                                placeholder="Postcode"
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label>Country</Label>
                            <Input
                              value={branch.branchCountry}
                              onChange={(e) => updateBranch(branch.id, 'branchCountry', e.target.value)}
                              placeholder="Country"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label>Branch Email</Label>
                            <Input
                              type="email"
                              value={branch.branchEmail}
                              onChange={(e) => updateBranch(branch.id, 'branchEmail', e.target.value)}
                              placeholder="branch@example.com"
                            />
                          </div>

                        </CardContent>
                      </Card>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recurring Orders Panel - Only shown when editing existing customers */}
          {editingCustomer && (
            <RecurringOrdersPanel customerId={editingCustomer.id} />
          )}

          {/* Credit Notes Panel - Only shown when editing existing customers */}
          {editingCustomer && (
            <CustomerCreditNotesPanel customerId={editingCustomer.id} />
          )}

          {/* Server Error Banner (submit errors only - validation errors shown at top) */}
          {errors.submit && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <p>{errors.submit}</p>
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter className="gap-2 flex-col sm:flex-row items-stretch sm:items-center">
            {editingCustomer && !isArchived && (
              <div className="sm:mr-auto">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-gray-600 hover:text-red-600 hover:bg-red-50 transition-colors"
                  size="sm"
                  onClick={() => setShowArchiveConfirm(true)}
                  disabled={loading}
                >
                  Archive Customer
                </Button>
              </div>
            )}
            {editingCustomer && isArchived && (
              <div className="sm:mr-auto">
                <Button
                  type="button"
                  variant="ghost"
                  className="text-gray-600 hover:text-green-600 hover:bg-green-50 transition-colors"
                  size="sm"
                  onClick={() => setShowUnarchiveConfirm(true)}
                  disabled={loading}
                >
                  Unarchive Customer
                </Button>
              </div>
            )}

            <div className="flex gap-2 sm:ml-auto">
              <Button type="button" variant="outline" onClick={() => onClose()}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant={editingCustomer ? "default" : "create"}
                disabled={loading}
                className={shakeSubmit ? 'animate-shake' : ''}
              >
                {loading ? 'Saving...' : editingCustomer ? 'Update Customer' : 'Add Customer'}
              </Button>
            </div>
          </DialogFooter>
        </form>

        {/* Archive Confirmation Dialog */}
        {showArchiveConfirm && (
          <Dialog open={showArchiveConfirm} onOpenChange={setShowArchiveConfirm}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Archive Customer</DialogTitle>
              </DialogHeader>
              <div className="py-4">
                <p className="text-sm text-gray-600">
                  Are you sure you want to archive this customer?
                </p>
                <br />
                <p className="text-sm text-gray-600">
                  Archived customers will be hidden from the active customer list but their sales records and payment history will be preserved.
                </p>
              </div>
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowArchiveConfirm(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  variant="destructive"
                  onClick={handleArchiveCustomer}
                  disabled={loading}
                >
                  {loading ? 'Archiving...' : 'Archive Customer'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Unarchive Confirmation Dialog */}
        {showUnarchiveConfirm && (
          <Dialog open={showUnarchiveConfirm} onOpenChange={setShowUnarchiveConfirm}>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Unarchive Customer</DialogTitle>
              </DialogHeader>
              <div className="py-4">
                <p className="text-sm text-gray-600">
                  Are you sure you want to unarchive this customer?
                </p>
                <br />
                <p className="text-sm text-gray-600">
                  They will be moved back to the active customer list and will be available for new sales and invoices.
                </p>
              </div>
              <DialogFooter>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowUnarchiveConfirm(false)}
                >
                  Cancel
                </Button>
                <Button 
                  type="button" 
                  onClick={handleUnarchiveCustomer}
                  disabled={loading}
                >
                  {loading ? 'Unarchiving...' : 'Unarchive Customer'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

      </DialogContent>
    </Dialog>

    {/* Payment Integration Modal - Shown after customer creation or when setting up payment */}
    <PaymentIntegrationModal
      isOpen={showPaymentIntegrationModal}
      onClose={() => {
        setShowPaymentIntegrationModal(false)
        setCustomerForIntegration(null)
        // Only close parent form if this was a new customer creation
        if (!editingCustomer) {
          onClose()
        }
      }}
      customer={customerForIntegration}
      onIntegrationComplete={async (updatedCustomer) => {
        // Refetch fresh customer data to get updated Xero mappings
        if (editingCustomer) {
          try {
            const freshCustomer = await loadFreshCustomerData(updatedCustomer.id)
            // Update formData with fresh data including Xero mappings
            setFormData(prev => ({
              ...prev,
              paymentMethod: freshCustomer.paymentMethod,
              branches: freshCustomer.branches // Update branches with fresh Xero mapping data
            }))
          } catch (error) {
            console.error('[CustomerForm] Failed to refresh customer data after integration:', error)
            // Fallback: just update payment method
            setFormData(prev => ({
              ...prev,
              paymentMethod: updatedCustomer.payment_method
            }))
          }
        } else {
          // New customer: just update payment method
          setFormData(prev => ({
            ...prev,
            paymentMethod: updatedCustomer.payment_method
          }))
        }

        setShowPaymentIntegrationModal(false)
        setCustomerForIntegration(null)

        // Only close parent form if this was a new customer creation
        if (!editingCustomer) {
          await onSave?.(updatedCustomer)
          onClose()
        }
      }}
    />
  </>
  )
}
